import { get } from 'lodash';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { getPath } from '@/router/utils';
import { ORDERED_INDUSTRIES, PRIMARY_INDUSTRIES } from '@/constants/industries';
import { getMarketIndustryForIdeas } from '@simplywallst/services';
import {
  ROUTE_INVESTING_IDEA_MARKET,
  ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
} from '@/constants/routes';
import { parseRules, updateMarketRule } from '@/pages/Screener/functions';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { isValidGridRule } from '@/utilities/grid';

type IndustryOptions = {
  option: string;
  label: string;
  href: string;
};

export const updateRules = (
  rules: string,
  market: PrimaryCountryISOElseGlobal
) => {
  let parsedRules = parseRules(rules);
  if (typeof market !== 'undefined' && market !== 'global') {
    parsedRules = updateMarketRule([market], [], parsedRules).filter(
      ([rule]) => rule !== 'exchange_symbol'
    );
  }
  return JSON.stringify(parsedRules);
};

type Options = {
  rules?: string;
  market?: PrimaryCountryISOElseGlobal;
  id?: string;
  name?: string;
};

const getOptions = ({
  market,
  id,
  name,
  industries = [],
}: Required<Omit<Options, 'rules'>> & {
  industries: string[];
}): IndustryOptions[] => [
  {
    option: 'any',
    label: 'Any',
    href: getPath({
      type: ROUTE_INVESTING_IDEA_MARKET,
      payload: {
        id,
        name,
        market,
      },
    }),
  },
  ...industries.map((industry) => {
    const industrySlug = get(PRIMARY_INDUSTRIES, [industry, 'slug'], 'any');
    const label = get(PRIMARY_INDUSTRIES, [industry, 'label'], 'any');
    return {
      option: industrySlug,
      label,
      href: getPath({
        type: ROUTE_INVESTING_IDEA_MARKET_INDUSTRY,
        payload: {
          id,
          name,
          market,
          industry: industrySlug,
        },
      }),
    };
  }),
];

const getQueryKey = (rules?: string) => [
  'market-industry-investing-ideas',
  rules,
];

/**
 * ### Description
 *
 * This hook takes a grid rule (screener rule) and checks to see what industries fall within that filter.
 * It aims to take the repsonse industry list and return a derived list of options ready for use in the SWS dropdown.
 *
 */
export function useIndustryOptionsByGridRule({
  rules,
  market = 'global',
  id = '0',
  name = 'slug',
}: Options) {
  const {
    data = getOptions({
      market,
      id,
      name,
      industries: ORDERED_INDUSTRIES,
    }),
    status,
    fetchStatus,
  } = useQuery({
    queryKey: getQueryKey(rules),
    queryFn: async ({
      queryKey: [_key],
    }): Promise<IndustryOptions[] | undefined> => {
      if (typeof rules === 'undefined') return undefined;
      const response = await getMarketIndustryForIdeas({
        id: '0',
        rules: updateRules(rules, market),
      });

      return getOptions({
        market,
        id,
        name,
        industries: response.data.data.map((i) => i.id.toString()),
      });
    },
    staleTime: Infinity,
    placeholderData: keepPreviousData,
    retry: 3,
    enabled: isValidGridRule(rules),
  });

  return {
    status,
    fetchStatus,
    industryOptions: data,
  };
}
